// src/hooks/useModulosFeedback.jsx
import { useState, useEffect, useCallback } from "react";
import { getLuzesStatusFlex8, statusDasLuzesFlex8 } from "../API/basic/FeedBackLuzes";

/**
 * Hook para gerenciar o feedback de status de todos os módulos
 * @param {number} intervalMs - Intervalo de atualização em milissegundos (padrão: 3000)
 * @param {boolean} autoStart - Se deve iniciar automaticamente (padrão: true)
 * @returns {Object} Estado e funções para controle dos módulos
 */
export const useModulosFeedback = (intervalMs = 3000, autoStart = true) => {
  const [modulosStatus, setModulosStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  // Função para buscar status dos módulos
  const fetchModulosStatus = useCallback(async () => {
    try {
      setError(null);
      await getLuzesStatusFlex8();
      console.log([...statusDasLuzesFlex8])
      // Usar o estado global do statusDasLuzesFlex8
      setModulosStatus([...statusDasLuzesFlex8]);
      setLastUpdate(new Date());
      
    } catch (err) {
      console.error("Erro ao buscar status dos módulos:", err);
      setError(err.message || "Erro ao buscar status dos módulos");
    } finally {
      setLoading(false);
    }
  }, []);

  // Função para obter status de um módulo específico
  const getModuloStatus = useCallback((moduloIndex) => {
    if (!modulosStatus[moduloIndex]) return null;
    return modulosStatus[moduloIndex];
  }, [modulosStatus]);

  // Função para obter status de um circuito específico
  const getCircuitoStatus = useCallback((modulo, idx) => {
    const moduloIndex = parseInt(modulo) - 1;
    const idxIndex = parseInt(idx) - 1;
    
    if (!modulosStatus[moduloIndex] || !modulosStatus[moduloIndex].dados) {
      return null;
    }
    
    return modulosStatus[moduloIndex].dados[idxIndex] === "ON";
  }, [modulosStatus]);

  // Função para verificar se um grupo específico está ligado
  const isGrupoLigado = useCallback((grupoConfig) => {
    if (!grupoConfig || !grupoConfig.modulo || !grupoConfig.idx) {
      return false;
    }
    
    return getCircuitoStatus(grupoConfig.modulo, grupoConfig.idx);
  }, [getCircuitoStatus]);

  // Função para forçar atualização manual
  const forceUpdate = useCallback(() => {
    setLoading(true);
    fetchModulosStatus();
  }, [fetchModulosStatus]);

  // Função para pausar/retomar atualizações automáticas
  const [isPaused, setIsPaused] = useState(!autoStart);
  
  const pauseUpdates = useCallback(() => setIsPaused(true), []);
  const resumeUpdates = useCallback(() => setIsPaused(false), []);

  // Effect para atualizações automáticas
  useEffect(() => {
    if (!autoStart && !isPaused) return;
    
    // Busca inicial
    fetchModulosStatus();

    // Configurar intervalo apenas se não estiver pausado
    if (!isPaused) {
      const interval = setInterval(fetchModulosStatus, intervalMs);
      return () => clearInterval(interval);
    }
  }, [fetchModulosStatus, intervalMs, autoStart, isPaused]);

  // Função para obter estatísticas dos módulos
  const getEstatisticas = useCallback(() => {
    const stats = {
      totalModulos: modulosStatus.length,
      totalCircuitos: 0,
      circuitosLigados: 0,
      circuitosDesligados: 0
    };

    modulosStatus.forEach(modulo => {
      if (modulo.dados && Array.isArray(modulo.dados)) {
        stats.totalCircuitos += modulo.dados.length;
        modulo.dados.forEach(status => {
          if (status === "ON") {
            stats.circuitosLigados++;
          } else if (status === "OFF") {
            stats.circuitosDesligados++;
          }
        });
      }
    });

    return stats;
  }, [modulosStatus]);

  return {
    // Estados
    modulosStatus,
    loading,
    error,
    lastUpdate,
    isPaused,
    
    // Funções de consulta
    getModuloStatus,
    getCircuitoStatus,
    isGrupoLigado,
    getEstatisticas,
    
    // Funções de controle
    forceUpdate,
    pauseUpdates,
    resumeUpdates,
    
    // Dados brutos para casos específicos
    statusRaw: statusDasLuzesFlex8
  };
};